import { inject } from '@angular/core';
import { Route } from '@angular/router';
import { authGuard } from '@elite/authentication/domain';
import { HasPermissionAdvancedGuard, PermissionCategory, PermissionAction } from '@elite/authentication/domain';
import { NotfoundComponent, SUPPORT_CALLS_ENABLED } from '@elite/layout';
import { LandingComponent } from './landing/landing.component';

/**
 * Strongly typed interface for route data with permissions
 */
interface PermissionRouteData {
  category: PermissionCategory;
  permission: PermissionAction;
}

/**
 * Helper function to create strongly typed route data
 */
function createPermissionData(category: PermissionCategory, permission: PermissionAction): PermissionRouteData {
  return { category, permission };
}

export const appRoutes: Route[] = [
  {
    path: '',
    redirectTo: 'landing',
    pathMatch: 'full',
  },
  {
    path: 'landing',
    component: LandingComponent,
    canActivate: [authGuard],
  },
  {
    path: 'login',
    loadChildren: () => import('@elite/authentication/feature-shell').then((mod) => mod.shellAuthRoutes),
  },
  {
    path: 'profile',
    loadChildren: () => import('@elite/profile/feature-shell').then((mod) => mod.profileRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'customers',
    loadChildren: () => import('@elite/customer/feature-shell-customer').then((mod) => mod.CUSTOMER_SHELL_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('customers', 'View'),
  },
  {
    path: 'operation-areas',
    loadChildren: () => import('@elite/operation-areas/feature-shell').then((mod) => mod.shellOperationAreasRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('operation-areas', 'View'),
  },
  {
    path: 'roles',
    loadChildren: () => import('@elite/roles/feature-role-shell').then((mod) => mod.shellRolesRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('roles', 'View'),
  },
  {
    path: 'zones',
    loadChildren: () => import('@elite/zones/feature-shell').then((mod) => mod.shellZoneRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('zones', 'View'),
  },
  {
    path: 'fixed-rates',
    loadChildren: () => import('@elite/fixed-rates/feature-shell').then((mod) => mod.FixedRateShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('fixed-rates', 'View'),
  },
  {
    path: 'regular-rate-config',
    loadChildren: () =>
      import('@elite/regular-rate-config/feature-shell').then((mod) => mod.RegularRateConfigShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('regular-rate-config', 'View'),
  },
  {
    path: 'cancellation-policies',
    loadChildren: () =>
      import('@elite/cancellation-policy/feature-shell').then((mod) => mod.CancellationPolicyShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('cancellation-policies', 'View'),
  },
  {
    path: 'commission-policies',
    loadChildren: () => import('@elite/commission-policy/feature-shell').then((mod) => mod.CommissionPolicyShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('commission-policies', 'View'),
  },
  {
    path: 'regular-rates',
    loadChildren: () => import('@elite/regular-rate/feature-shell').then((mod) => mod.RegularRateShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('regular-rate', 'View'),
  },
  {
    path: 'dynamic-rates',
    loadChildren: () => import('@elite/dynamic-rate/feature-shell').then((mod) => mod.DynamicRateShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('dynamic-rates', 'View'),
  },
  {
    path: 'dispatch-settings',
    loadChildren: () =>
      import('@elite/dispatch-settings/feature-shell').then((mod) => mod.dispatchSettingsFeatureShellRoute),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('dispatch-settings', 'View'),
  },
  {
    path: 'global-settings',
    loadChildren: () => import('@elite/global-settings/feature-shell').then((mod) => mod.globalSettingsFeatureShellRoute),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('global-settings', 'View'),
  },
  {
    path: 'users',
    loadChildren: () => import('@elite/users/feature-shell').then((mod) => mod.shellUserRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('users', 'View'),
  },
  {
    path: 'car-color',
    loadChildren: () => import('@elite/car-color/feature-shell').then((mod) => mod.shellCarColorRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('car-color', 'View'),
  },
  {
    path: 'car-make',
    loadChildren: () => import('@elite/car-make/feature-shell').then((mod) => mod.CarMakeShellRoute),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('car-make', 'View'),
  },
  {
    path: 'car-model',
    loadChildren: () => import('@elite/car-model/feature-shell').then((mod) => mod.CarModelShellRoute),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('car-model', 'View'),
  },
  {
    path: 'company',
    loadChildren: () => import('@elite/company/feature-shell').then((mod) => mod.CompanyShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('company', 'View'),
  },
  {
    path: 'fleet',
    loadChildren: () => import('@elite/fleet/feature-shell').then((mod) => mod.ShellFleetRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('fleet', 'View'),
  },
  {
    path: 'ride',
    loadChildren: () => import('@elite/ride/feature-ride-shell').then((mod) => mod.RIDE_SHELL_ROUTES),
  },
  {
    path: 'ride-alerts',
    loadChildren: () => import('@elite/ride/feature-ride-alert-shell').then((mod) => mod.RIDE_ALERT_SHELL_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('ride', 'ViewWarnings'),
  },
  {
    path: 'ride-class',
    loadChildren: () => import('@elite/ride-class/feature-shell').then((mod) => mod.ShellRideClassRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('ride-class', 'View'),
  },
  {
    path: 'ride-option',
    loadChildren: () =>
      import('@elite/ride-option-type/feature-ride-option-type-shell').then((mod) => mod.RideOptionTypeShellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('ride-option-type', 'View'),
  },
  {
    path: 'cars',
    loadChildren: () => import('@elite/car/feature-shell-car').then((mod) => mod.ShellCarRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('cars', 'View'),
  },
  {
    path: 'drivers',
    loadChildren: () => import('@elite/driver/feature-driver-shell').then((mod) => mod.DRIVER_SHELL),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('drivers', 'View'),
  },
  {
    path: 'driver-reviews',
    loadChildren: () => import('@elite/driver-review/shell').then((mod) => mod.DRIVER_REVIEW_SHELL),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('driver-reviews', 'View'),
  },
  {
    path: 'ride-review-template',
    loadChildren: () =>
      import('@elite/ride-review-template/shell').then((mod) => mod.RIDE_REVIEW_TEMPLATE_SHELL_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('ride-review-template', 'View'),
  },
  {
    path: 'wallets',
    loadChildren: () => import('@elite/wallets/feature-wallet-shell').then((mod) => mod.Wallet_Shell_Routes),
    canActivate: [authGuard],
  },
  {
    path: 'transactions',
    loadChildren: () => import('@elite/transactions/feature-transaction-shell').then((mod) => mod.TransactionRoutes),
    canActivate: [authGuard],
  },
  {
    path: 'localizations',
    loadChildren: () =>
      import('@elite/localizations/manage-localizations-list').then((mod) => mod.MANAGE_LOCALIZATIONS_LIST_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('localizations', 'View'),
  },
  {
    path: 'corporations',
    loadChildren: () => import('@elite/corporations/feature-shell').then((mod) => mod.shellRoute),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('corporations', 'View'),
  },
  {
    path: 'corporation-wallets',
    loadChildren: () => import('@elite/corporation-wallet/feature-shell').then((mod) => mod.routes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('corporation-wallets', 'View'),
  },
  {
    path: 'campaigns',
    loadChildren: () => import('@elite/campaign/feature-shell').then((m) => m.shellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('campaigns', 'View'),
  },
  {
    path: 'incentives',
    loadChildren: () => import('@elite/incentive/feature-shell').then((m) => m.INCENTIVE_SHELL_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('incentives', 'View'),
  },
  {
    path: 'announcements',
    loadChildren: () => import('@elite/announcement/feature-shell').then((m) => m.shellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('announcements', 'View'),
  },
  {
    path: 'tickets',
    loadChildren: () => import('@elite/ticket/feature-shell').then((m) => m.TICKET_SHELL_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('tickets', 'View'),
  },
  {
    path: 'ticket-chat-template',
    loadChildren: () =>
      import('@elite/ticket-chat-template/shell').then((mod) => mod.TICKET_CHAT_TEMPLATE_SHELL_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('ticket-chat-template', 'View'),
  },
  {
    path: 'promo-codes',
    loadChildren: () => import('@elite/promo-code/feature-shell').then((m) => m.PROMO_CODE_SHELL_ROUTES),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('promo-code', 'View'),
  },
  {
    path: 'promo-rules',
    loadChildren: () => import('@elite/promo-rule/feature-shell').then((m) => m.SHELL_PROMO_RULE_ROUTE),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('promo-rule', 'View'),
  },
  {
    path: 'reports',
    loadChildren: () => import('@elite/report/feature-shell').then((m) => m.shellRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('reports', 'View'),
  },
  {
    path: 'notifications',
    loadChildren: () => import('@elite/notification/feature-shell').then((m) => m.notificationFeatureShellRoute),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    data: createPermissionData('drivers', 'View'),
  },
  {
    path: 'support-calls',
    // Off behind `SUPPORT_CALLS_ENABLED`, the route does not match and the URL falls through to not-found.
    canMatch: [() => inject(SUPPORT_CALLS_ENABLED)],
    loadChildren: () => import('@elite/realtime/feature-support-calls').then((m) => m.realtimeSupportCallsRoutes),
    canActivate: [authGuard, HasPermissionAdvancedGuard],
    // `CallPermissions.HandleSupport` (58000) — what `AcceptCall` and `SetAgentAvailability` require.
    // The same pair gates the component and the menu entry.
    data: createPermissionData('calls', 'HandleSupport'),
  },
  {
    path: 'no-access',
    component: NotfoundComponent,
  },
  { path: '**', pathMatch: 'full', component: NotfoundComponent },
];
